import { useTranslation } from "react-i18next";

/**
 * Top anchor component for media attachments
 * Used as an intersection target to trigger loading more attachments
 *
 * How it works:
 * 1. Provides a reference point for the intersection observer
 * 2. Shows loading text when more content is being loaded
 * 3. Remains empty when all content is loaded
 *
 * Key features:
 * - Seamless integration with infinite scrolling
 * - Localized loading text through i18n
 * - Minimal visual footprint when not loading
 *
 * Context:
 * This component is used by MediaAttachmentList as the target for the intersection
 * observer to trigger loading more media attachments when scrolling up.
 */
interface MediaAttachmentAnchorProps {
  anchorRef: (instance: HTMLDivElement | null) => void;
  isFullListLoaded: boolean;
  hasAttachments: boolean;
}

export default function MediaAttachmentAnchor({
  anchorRef,
  isFullListLoaded,
  hasAttachments,
}: MediaAttachmentAnchorProps) {
  const { t } = useTranslation();

  return (
    <div ref={anchorRef} className="text-md w-full p-2 text-center">
      {!isFullListLoaded && hasAttachments ? t("loading", "Loading...") : ""}
    </div>
  );
}
